import { z } from 'zod';

import type { GeocodingPlace } from '@sshop/domain';

import { parseDto } from '../mappers/parseDto';

const placeDto = z.object({
  place_id: z.number(),
  display_name: z.string(),
  lat: z.string(),
  lon: z.string(),
});

function toPlace(dto: z.infer<typeof placeDto>): GeocodingPlace {
  return {
    placeId: dto.place_id,
    displayName: dto.display_name,
    latitude: Number(dto.lat),
    longitude: Number(dto.lon),
  };
}

export interface GeocodingService {
  search(
    query: string,
    cityLabel: string,
    language: string,
    signal: AbortSignal,
  ): Promise<GeocodingPlace[]>;
  reverse(
    latitude: number,
    longitude: number,
    language: string,
    signal: AbortSignal,
  ): Promise<GeocodingPlace>;
}

export function createGeocodingService(): GeocodingService {
  return {
    async search(query, cityLabel, language, signal) {
      const params = new URLSearchParams({
        q: [query, cityLabel, 'Georgia'].filter(Boolean).join(', '),
        format: 'jsonv2',
        addressdetails: '1',
        countrycodes: 'ge',
        limit: '5',
      });
      const response = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
        signal,
        headers: { 'Accept-Language': language },
      });
      if (!response.ok) throw new Error('Address search failed');
      return parseDto(z.array(placeDto), await response.json(), 'GET Nominatim /search').map(
        toPlace,
      );
    },
    async reverse(latitude, longitude, language, signal) {
      const params = new URLSearchParams({
        lat: String(latitude),
        lon: String(longitude),
        format: 'jsonv2',
        addressdetails: '1',
        zoom: '18',
      });
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params}`, {
        signal,
        headers: { 'Accept-Language': language },
      });
      if (!response.ok) throw new Error('Reverse geocoding failed');
      return toPlace(parseDto(placeDto, await response.json(), 'GET Nominatim /reverse'));
    },
  };
}
